import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND } from '@rydres/shared';
import { Badge, Button, Field, InfoTip, Input, Notice, toE164 } from '@rydres/ui';
import { chatApi, driverApi } from '../lib/api.js';
import { lastLine, useConversations, when } from '../lib/chat.js';
import { pushState, type PushState } from '../lib/push.js';
import type { ConversationSummary, Friend } from '../lib/types.js';
import { ContactButtons, EarlierConversations, UnreadCount } from './Conversation.js';
import { PassengersPanel } from './Passengers.js';

const TABS = [
  { key: 'passengers', label: 'Passengers' },
  { key: 'drivers', label: 'Drivers' },
] as const;

/** The people a driver works with: passengers and driver friends, each with Message and Call. Conversations open on top. */
export function People() {
  const [params, setParams] = useSearchParams();
  const convos = useConversations();
  const chat = params.get('chat');
  // A notification opens a conversation without a tab: show the list it belongs to.
  const tab: (typeof TABS)[number]['key'] =
    TABS.find((t) => t.key === params.get('tab'))?.key ?? (convos?.find((c) => c.id === chat)?.kind === 'friend' ? 'drivers' : TABS[0].key);
  const [push, setPush] = useState<PushState | null>(null);
  useEffect(() => {
    pushState().then(setPush, () => setPush(null));
  }, []);
  const unreadIn = (key: string) => convos?.filter((c) => (c.kind === 'friend') === (key === 'drivers')).reduce((n, c) => n + c.unread, 0) ?? 0;
  const pick = (key: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set('tab', key);
        return next;
      },
      { replace: true },
    );
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">People</h1>
      {push && push !== 'on' && convos && convos.length > 0 && (
        <Notice tone="warn">
          Notifications are off on this phone, so new messages wait here until you look.{' '}
          <Link className="font-bold underline" to="/settings/notifications">Turn them on</Link>
        </Notice>
      )}
      <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => pick(t.key)} aria-pressed={tab === t.key} className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 ${tab === t.key ? 'bg-card shadow-card' : 'text-muted'}`}>
            {t.label}
            <UnreadCount n={unreadIn(t.key)} />
          </button>
        ))}
      </div>
      {tab === 'passengers' && <PassengersPanel convos={convos} />}
      {tab === 'drivers' && <DriverFriends convos={convos} />}
    </div>
  );
}

function Avatar({ name, url }: { name: string | null; url: string | null }) {
  return url ? (
    <img src={url} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
  ) : (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand font-black text-on-brand">{(name ?? '?').slice(0, 1)}</div>
  );
}

/** A page name, a rydres.com link or a phone number. */
function parseWho(input: string): { handle: string } | { phone: string } | null {
  const v = input.trim();
  if (!v) return null;
  const digits = v.replace(/\D/g, '');
  if (digits.length >= 10 && /^[+\d\s().-]+$/.test(v)) {
    const phone = toE164(v);
    return phone ? { phone } : null;
  }
  return { handle: v.replace(/^(https?:\/\/)?(www\.)?rydres\.com\//i, '').replace(/\/.*$/, '').toLowerCase() };
}

function DriverFriends({ convos }: { convos: ConversationSummary[] | null }) {
  const [list, setList] = useState<Friend[] | null>(null);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    driverApi.friends().then((r) => setList(r.friends), () => setList([]));
  }, []);
  const run = async (key: string, fn: () => Promise<{ friends: Friend[] }>) => {
    setBusy(key);
    setError(null);
    try {
      setList((await fn()).friends);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
      return false;
    } finally {
      setBusy(null);
    }
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    const target = parseWho(who);
    if (!target) return setError('Enter their page name (rydres.com/name) or their phone number.');
    if (await run('add', () => driverApi.addFriend(target))) setWho('');
  };
  const asking = list?.filter((f) => f.waitingFor === 'me') ?? [];
  const friends = list?.filter((f) => f.status === 'accepted') ?? [];
  const waiting = list?.filter((f) => f.waitingFor === 'them') ?? [];
  const withDrivers = convos?.filter((c) => c.kind === 'friend') ?? [];
  const byFriend = new Map(withDrivers.map((c) => [c.friendId, c]));
  const friendIds = new Set(friends.map((f) => f.driver.id));
  const earlier = list ? withDrivers.filter((c) => !c.friendId || !friendIds.has(c.friendId)) : [];

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => void add(e)} className="space-y-2">
        <Field label="Add a driver friend" hint="Their RydRes page name, rydres.com link or phone. They get a request to accept.">
          <Input value={who} onChange={(e) => setWho(e.target.value)} placeholder="rydres.com/name or (714) 555-0100" autoCapitalize="none" autoCorrect="off" />
        </Field>
        <Button type="submit" small disabled={!who.trim() || busy !== null}>{busy === 'add' ? 'Sending…' : 'Send request'}</Button>
      </form>
      {error && <Notice tone="danger">{error}</Notice>}

      {asking.length > 0 && (
        <section className="space-y-2">
          <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Asking you</h2>
          {asking.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-2xl border border-line bg-card p-3">
              <Avatar name={f.driver.displayName} url={f.driver.photoUrl} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">{f.driver.displayName}</div>
                <div className="truncate text-xs text-muted">{f.driver.legalName} · {f.driver.tcpNumber ?? 'no TCP number yet'}</div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button small disabled={busy !== null} onClick={() => void run(f.id, () => driverApi.acceptFriend(f.id))}>Accept</Button>
                <Button small variant="ghost" disabled={busy !== null} onClick={() => void run(f.id, () => driverApi.removeFriend(f.id))}>Decline</Button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <h2 className="flex items-center gap-1 px-1 text-xs font-bold uppercase tracking-wide text-muted">
          Your driver friends
          <InfoTip label="driver friends">Drivers you trust. When you are booked or off, your page suggests them to riders, who book them directly on their own page. No job or payment passes between you.</InfoTip>
        </h2>
        {list === null ? (
          <p className="px-1 text-sm text-muted">Loading…</p>
        ) : friends.length === 0 ? (
          <p className="px-1 text-sm text-muted">None yet. Add a driver you trust above.</p>
        ) : (
          friends.map((f) => {
            const c = byFriend.get(f.driver.id) ?? null;
            return (
            <div key={f.id} className="space-y-3 rounded-2xl border border-line bg-card p-3">
              <div className="flex items-center gap-3">
                <Avatar name={f.driver.displayName} url={f.driver.photoUrl} />
                <div className="min-w-0 flex-1">
                  <div className={`flex items-center gap-2 truncate ${c?.unread ? 'font-black' : 'font-bold'}`}>{f.driver.displayName}{!f.driver.pageLive && <Badge value="page not live" tone="muted" />}</div>
                  <div className="truncate text-xs text-muted">{f.driver.tcpNumber ?? 'no TCP number yet'}{f.driver.handle && <> · <a className="underline" href={`${BRAND.publicOrigin}/${f.driver.handle}`} target="_blank" rel="noreferrer">rydres.com/{f.driver.handle}</a></>}</div>
                  {c?.last && <div className="truncate text-sm text-ink2">{when(c.last.at)} · {lastLine(c)}</div>}
                </div>
                {c && <UnreadCount n={c.unread} />}
              </div>
              <ContactButtons
                name={f.driver.displayName ?? 'this driver'}
                phone={f.driver.phone}
                conversationId={c?.id ?? null}
                canMessage={f.canMessage}
                open={() => chatApi.open({ friendId: f.driver.id })}
              />
              <div className="flex items-center justify-between gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={f.showOnMyPage} disabled={busy !== null} onChange={(e) => void run(f.id, () => driverApi.showFriend(f.id, e.target.checked))} />
                  Suggest on my page when I'm busy
                </label>
                <button disabled={busy !== null} onClick={() => void run(f.id, () => driverApi.removeFriend(f.id))} className="text-xs font-bold text-danger">Remove</button>
              </div>
            </div>
            );
          })
        )}
      </section>

      <EarlierConversations list={earlier} />

      {waiting.length > 0 && (
        <section className="space-y-2">
          <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Waiting for them to accept</h2>
          {waiting.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-2xl border border-dashed border-line p-3">
              <Avatar name={f.driver.displayName} url={f.driver.photoUrl} />
              <div className="min-w-0 flex-1 truncate font-bold">{f.driver.displayName}</div>
              <button disabled={busy !== null} onClick={() => void run(f.id, () => driverApi.removeFriend(f.id))} className="shrink-0 text-xs font-bold text-muted">Cancel</button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
